import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { DEV_BYPASS, useAuth } from '../lib/auth';
import { pageKey } from '../lib/pages';
import { installTableSorting } from '../lib/tableSort';
import { FeatureRequestButton } from './FeatureRequestButton';
import { IdleSignOut } from './IdleSignOut';
import type { Me } from '../lib/types';
import cmitLogo from '../assets/cmit-logo.png';

/**
 * A link in the sidebar.
 *
 * Who sees it is decided by the employee record's page list (migration 144):
 * a non-admin sees exactly the pages ticked for them, by `pageKey(to)`. The
 * Employees screen draws its checklist from this same NAV, so the menu and
 * the checklist cannot drift apart.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Index route only — see the note on Dashboard. */
  end?: boolean;
  /** Admins only, never assignable. */
  adminOnly?: boolean;
}

export interface NavGroup {
  section: string;
  /** Admins only, the whole section, heading included. */
  adminOnly?: boolean;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    section: 'Overview',
    /* `end` matters on the index route: without it NavLink treats "/" as a
       prefix of every path and the Dashboard link stays highlighted on every
       page. */
    items: [{ to: '/', label: 'Dashboard', icon: '▚', end: true }],
  },
  {
    /* The planning instruments — the plan, the charter, the maturity
       assessment, the business review and the ownership map. Until 144 the
       whole section followed the Leadership flag; now each page is ticked per
       person like any other. */
    section: 'Vision',
    items: [
      { to: '/vto', label: 'V/TO', icon: '◈' },
      { to: '/charter', label: 'COO Charter', icon: '§' },
      { to: '/oml', label: 'OML Assessment', icon: '◱' },
      /* Under the OML Assessment because its filter is the same ladder: each
         scorecard item is placed at the OML level where it is first measured. */
      { to: '/kpi-wheel', label: 'KPI Wheel', icon: '✺' },
      /* EOS — the operating cadence, moved in from the standalone app. Directly
         under the V/TO and the charter because it is how the plan actually gets
         run week to week: its Rocks and Issues tabs read and write the V/TO's
         own rocks and issues, so grant it with that in mind. */
      { to: '/eos', label: 'EOS', icon: '⟳' },
      { to: '/reviews', label: 'Business Review', icon: '▤' },
      { to: '/dependency', label: 'Ownership Dependency', icon: '⚯' },
      /* Admin-only and not assignable: an acquisition in progress is not
         general knowledge, even among leadership. */
      { to: '/ma', label: 'M&A', icon: '⇉', adminOnly: true },
      /* The one part of M&A that can be granted (migration 163): the people
         working an integration see the board, less any list an admin keeps to
         admins. */
      { to: '/integration-plan', label: 'Integration Plan', icon: '▥' },
    ],
  },
  {
    /* Directly below Vision: who is accountable for what, and how they are
       doing at it, is the same conversation as the plan. */
    section: 'People',
    items: [
      { to: '/chart', label: 'Accountability Chart', icon: '⛁' },
      { to: '/gwc', label: 'GWC Assessment', icon: '◎' },
      { to: '/people', label: 'People', icon: '☺' },
      /* Its own page, not a tab of People, so a hiring manager can be given
         it without everybody's profile (migration 174). */
      { to: '/recruiting', label: 'Recruiting', icon: '✚' },
    ],
  },
  {
    section: 'Revenue',
    items: [
      { to: '/sales', label: 'Sales', icon: '◇' },
      { to: '/account-management', label: 'Account Management', icon: '◐' },
      /* The price book both of the above quote from. Last in the group because
         it is the reference the other two use rather than a desk of its own;
         its API also answers to either of them (server.ts). */
      { to: '/products', label: 'Product Catalogue', icon: '☷' },
    ],
  },
  {
    section: 'Operate',
    items: [
      { to: '/service-delivery', label: 'Service Delivery', icon: '▦' },
      /* Directly under Service Delivery: CSAT is where one of that page's six
         gauges comes from. */
      { to: '/csat', label: 'CSAT', icon: '☻' },
      /* The CS Toolbox, moved in. Audits, the tools launcher and the vendor
         directory. */
      { to: '/cs', label: 'CS', icon: '◫' },
      /* The Professional Services portal, moved in. Scoped engagements. */
      { to: '/projects', label: 'Projects', icon: '▩' },
      /* The NOC alert knowledge base, moved in. */
      { to: '/noc', label: 'NOC', icon: '◉' },
      /* The onboarding task library. Under Operate because that is who runs
         it — the library is the standing list of what happens when a client
         arrives, not a sales artefact. Its endpoints also answer to Sales and
         Account Management; see the mount in server.ts. */
      { to: '/client-onboarding', label: 'Client Onboarding', icon: '⊞' },
      { to: '/operations-manual', label: 'Operations Manual', icon: '❋' },
      { to: '/compliance', label: 'Compliance', icon: '⛨' },
      /* The operations admin dashboard, moved in. Last in this group because it
         is the back office behind the rest of it — contracts, procurement, the
         depot, the SOP register and the paperwork. */
      { to: '/admin', label: 'Admin', icon: '▤' },
    ],
  },
  {
    /* The desk's own tools rather than a function of the business: raising a
       change to how things are done, checking a ticket export against the
       rules, and asking for training. */
    section: 'Tools',
    items: [
      { to: '/change-requests', label: 'Change Requests', icon: '⇌' },
      { to: '/ticket-audit', label: 'Ticket Audit', icon: '⊘' },
      /* The training policy's two forms, filled in and exported to PDF. */
      { to: '/training-request', label: 'Training Request', icon: '✎' },
    ],
  },
  {
    /* Dashboards read live from Autotask through the connection under
       Settings › Integrations. Each is its own grantable page. */
    section: 'Reports',
    items: [
      { to: '/quotes', label: 'Quotes', icon: '❏' },
      /* Profitability for CMIT of Livermore and the companies under it. */
      { to: '/sdf', label: 'SDF', icon: '◪' },
      /* Autotask's pre-posting details by work type, live: time and charges not yet posted. */
      { to: '/preposting', label: 'Pre-posting', icon: '⧗' },
      /* Every ticket time entry for the days chosen — to find time on CMIT Solutions or FIX tickets. */
      { to: '/misfits', label: 'Misfits', icon: '⚑' },
      /* One client's revenue, labor draw, cost and profit, with where every figure came from. */
      { to: '/profitability', label: 'Client Profitability', icon: '◩' },
      /* What every active client was invoiced, by date and client. */
      { to: '/invoices', label: 'Invoice Totals', icon: '▭' },
      /* How each contract is set up: exclusions, roles, work types, rates, issue types. */
      { to: '/contract-setup', label: 'Contract Setup', icon: '▧' },
      /* Hours by technician, billable by the roles and work types chosen there. */
      { to: '/billable-labor', label: 'Billable Labor', icon: '◷' },
      /* One technician's time entries judged on how they were kept: overlapping clocks, and how late each was written down. */
      { to: '/technician-cleanliness', label: 'Technician Cleanliness', icon: '◴' },
      /* Every active firewall: what it invoiced and made, when it renews and the profit on that. */
      { to: '/firewalls', label: 'Firewall Assessment', icon: '⛉' },
      /* Every active asset with the tickets raised against it and the hours put into them, for the days chosen. */
      { to: '/configuration-items', label: 'Asset Workload', icon: '▥' },
      /* One client's month as the twenty-five things a client wants to see: tickets, alerts, labor, hardware, projects, invoice items. */
      { to: '/client-report', label: 'Client Report', icon: '◉' },
      /* The twenty figures an MSP reports on, each with where it came from and the records behind it. */
      { to: '/msp-metrics', label: 'MSP Top 20', icon: '⊛' },
      /* A makeshift profit and loss for the year, by month, with the source of every line and the gaps named. */
      { to: '/pnl', label: 'PNL', icon: '⊟' },
      /* The eight managed-service items on every active contract, five years out at the price rises you set. */
      { to: '/managed-services', label: 'Managed Services Forecast', icon: '↗' },
    ],
  },
  {
    /* Last: what the app is CONFIGURED with rather than what it records — the
       shared directory every other tool in the estate reads its people from,
       and the list of what the business does.

       Admin-only item by item rather than as a section, because Employees is
       now grantable and the rest of this is not. Employees was held back
       because it is where access is granted — but granting it no longer grants
       anything: every write and every /access endpoint behind that page is
       still requireAdmin, and a non-admin's list is cut to themselves and the
       people under them (GET /employees?scope=directory). Everything else here
       carries its own flag, so a page added to this section has to say which
       it is rather than inheriting an answer. */
    section: 'Settings',
    items: [
      { to: '/employees', label: 'Employees', icon: '☰' },
      /* Processes, moved in from the standalone EOS app. Under the directory it
         names owners and departments from. */
      { to: '/processes', label: 'Processes', icon: '⚙', adminOnly: true },
      /* The library a process is measured out of, seeded from the EOS app.
         Directly under Processes, because it is the list they pick from. */
      { to: '/scorecard-items', label: 'Scorecard Items', icon: '◎', adminOnly: true },
      /* Connections to outside systems (Autotask …). Admin-only because it
         holds API secrets — matching requireAdmin on /api/integrations. */
      { to: '/integrations', label: 'Integrations', icon: '⇄', adminOnly: true },
      /* How Autotask's data is laid out — the reference behind every Autotask
         feature here. */
      { to: '/autotask-map', label: 'Autotask Data Map', icon: '⌗', adminOnly: true },
      /* The audit trail. Last, because it is the record OF everything above
         rather than another thing to configure — and here rather than in Admin
         because it is not something the admin function owns. Admin-only,
         matching requireAdmin on /api/audit. */
      { to: '/audit-trail', label: 'Audit trail', icon: '⧉', adminOnly: true },
      /* What people have asked the app to do, raised from the top bar of any
         page. Admin-only to READ: a request names the person who made it, and
         the list is for whoever decides what gets built, not for everyone it
         is about. */
      { to: '/feature-requests', label: 'Feature requests', icon: '✦', adminOnly: true },
    ],
  },
];

type Theme = 'light' | 'dark' | 'system';

function useTheme() {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('coo-theme') as Theme | null) ?? 'system',
  );

  useEffect(() => {
    const root = document.documentElement;
    /* 'system' means remove the attribute entirely and let the media query in
       tokens.css decide — stamping a value would override the OS preference. */
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    localStorage.setItem('coo-theme', theme);
  }, [theme]);

  return { theme, setTheme };
}

/* Pages that use the whole width of the window rather than the 1280px
   reading column — wide by nature, like an org chart. The whole People
   section is here, so its pages read alike, and so is Account Management,
   whose tabs are mostly wide tables. */
const FULL_WIDTH_PATHS = [
  '/chart',
  '/gwc',
  '/people',
  '/recruiting',
  '/account-management',
  '/autotask-map',
];

/** Whether the sidebar is folded to its icons, remembered in this browser. */
function useNavFolded() {
  const [folded, setFolded] = useState<boolean>(() => {
    try {
      return localStorage.getItem('coo-nav-folded') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('coo-nav-folded', folded ? '1' : '0');
    } catch {
      /* private mode: it simply is not remembered */
    }
  }, [folded]);
  return [folded, setFolded] as const;
}

/**
 * Which sidebar sections are rolled open, remembered in this browser. Every
 * section starts rolled up; the one holding the page you are on opens itself
 * whenever you arrive at a page in it, so the current page is never hidden.
 */
function useNavOpenSections(pathname: string) {
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('coo-nav-open');
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('coo-nav-open', JSON.stringify([...open]));
    } catch {
      /* private mode: it simply is not remembered */
    }
  }, [open]);
  useEffect(() => {
    const here = NAV.find((g) =>
      g.items.some((i) => (i.end ? pathname === i.to : pathname === i.to || pathname.startsWith(`${i.to}/`))),
    );
    if (here) setOpen((prev) => (prev.has(here.section) ? prev : new Set(prev).add(here.section)));
  }, [pathname]);
  const toggle = (section: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  return [open, toggle] as const;
}

export function Shell({ me }: { me: Me | null }) {
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const [folded, setFolded] = useNavFolded();
  const { pathname } = useLocation();
  const [openSections, toggleSection] = useNavOpenSections(pathname);
  const fullWidth = FULL_WIDTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  /* The menu label for wherever we are, for the feature request form to send
     with the path. Longest match wins, so a page under '/am/x' is named by
     '/am' rather than by '/', which prefixes everything. */
  const pageLabel =
    NAV.flatMap((g) => g.items)
      .filter((i) => (i.end ? pathname === i.to : pathname === i.to || pathname.startsWith(`${i.to}/`)))
      .sort((a, b) => b.to.length - a.to.length)[0]?.label ?? null;

  /* Click any data table's column heading to sort by it (lib/tableSort.ts). */
  useEffect(() => installTableSorting(), []);

  const cycle = () => setTheme(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system');
  const themeIcon = theme === 'light' ? '☀' : theme === 'dark' ? '☾' : '◐';

  return (
    <div className={`shell${folded ? ' nav-folded' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="name">{folded ? 'M' : 'MSP-OPS'}</div>
        </div>

        <nav className="nav">
          {NAV.map((group) => {
            /* A link to a page that will refuse to load is worse than no link:
               it reads as a broken app rather than as one you do not have. The
               API enforces the same rule, so hiding here is presentation, not
               the control itself. A whole section that empties out disappears
               with its heading rather than leaving a stranded label. */
            const isAdmin = me?.appRole === 'admin';
            if (group.adminOnly && !isAdmin) return null;

            /* The page list on their employee record (144); admins see all. */
            const items = group.items.filter((item) =>
              isAdmin ? true : !item.adminOnly && (me?.pages?.includes(pageKey(item.to)) ?? false),
            );
            if (items.length === 0) return null;
            /* Rolled up, the links stay in the page but hidden, so the folded
               icon rail and the narrow-screen top bar (app.css) can still show
               every one. */
            const isOpen = openSections.has(group.section);
            const listId = `nav-group-${group.section.toLowerCase()}`;
            return (
            <div key={group.section} className={`nav-group${isOpen ? ' open' : ''}`}>
              <button
                type="button"
                className="nav-section"
                onClick={() => toggleSection(group.section)}
                aria-expanded={isOpen}
                aria-controls={listId}
              >
                <span>{group.section}</span>
                <span className="nav-chevron" aria-hidden="true">
                  ▸
                </span>
              </button>
              <div className="nav-group-items" id={listId}>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={'end' in item ? item.end : false}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                  /* Folded, the label is hidden; the tooltip and the accessible
                     name still carry it. */
                  title={folded ? item.label : undefined}
                  aria-label={folded ? item.label : undefined}
                >
                  <span className="icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
              </div>
            </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user">{me?.displayName ?? 'Signed in'}</div>
          <div>{me?.appRole ?? '—'} access</div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm nav-fold-btn"
              onClick={() => setFolded((f) => !f)}
              title={folded ? 'Show the menu' : 'Fold the menu away'}
              aria-label={folded ? 'Show the menu' : 'Fold the menu away'}
              aria-pressed={folded}
            >
              {folded ? '»' : '«'}
            </button>
            <div className="topbar-logo">
              <img src={cmitLogo} alt="CMIT Solutions" />
            </div>
          </div>
          <div className="row">
            {/* Every page, because the moment to ask for something is while
                looking at the thing that prompted it. */}
            <FeatureRequestButton pageLabel={pageLabel} />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={cycle}
              title={`Theme: ${theme}`}
              aria-label={`Switch theme (currently ${theme})`}
            >
              {themeIcon}
            </button>
            {!DEV_BYPASS && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={signOut}>
                Sign out
              </button>
            )}
          </div>
        </div>

        {/* Signs out after 60 minutes without activity, with a warning first. */}
        <IdleSignOut />
        <main className={`content${fullWidth ? ' content-full' : ''}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
